import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X } from 'lucide-react';
import './MultiSelectFilter.css';

export default function MultiSelectFilter({ label, options, selected, onChange, className, ariaLabel }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const wrapperRef = useRef(null);
  const searchRef = useRef(null);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchText('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search when opened
  useEffect(() => {
    if (isOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen]);

  const filtered = options.filter(opt =>
    opt.name.toLowerCase().includes(searchText.toLowerCase())
  );

  const toggleOption = (id) => {
    const idStr = String(id);
    if (selected.includes(idStr)) {
      onChange(selected.filter(v => v !== idStr));
    } else {
      onChange([...selected, idStr]);
    }
  };

  const selectAll = () => {
    onChange(filtered.map(opt => String(opt.id)));
  };

  const clearAll = () => {
    onChange([]);
    setSearchText('');
  };

  // Build display text
  let displayText = `All ${label}`;
  if (selected.length === 1) {
    const match = options.find(o => String(o.id) === selected[0]);
    displayText = match ? match.name : `1 selected`;
  } else if (selected.length > 1) {
    displayText = `${label} (${selected.length})`;
  }

  const hasSelection = selected.length > 0;

  return (
    <div className={`msf-wrapper ${className || ''}`} ref={wrapperRef}>
      <button
        type="button"
        className={`msf-trigger ${hasSelection ? 'msf-trigger--active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={ariaLabel || `Filter by ${label}`}
        aria-expanded={isOpen}
      >
        <span className="msf-trigger-text">{displayText}</span>
        {hasSelection && (
          <span
            className="msf-clear"
            onClick={(e) => { e.stopPropagation(); clearAll(); }}
            role="button"
            aria-label={`Clear ${label} filter`}
          >
            <X size={14} />
          </span>
        )}
        <ChevronDown size={14} className={`msf-chevron ${isOpen ? 'msf-chevron--open' : ''}`} />
      </button>

      {isOpen && (
        <div className="msf-dropdown">
          {options.length > 6 && (
            <div className="msf-search">
              <input
                ref={searchRef}
                type="text"
                className="msf-search-input"
                placeholder="Search..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          )}
          <div className="msf-actions">
            <button type="button" className="msf-action-btn" onClick={selectAll}>Select All</button>
            <button type="button" className="msf-action-btn" onClick={clearAll}>Clear</button>
          </div>
          <div className="msf-options">
            {filtered.map(opt => {
              const idStr = String(opt.id);
              const isChecked = selected.includes(idStr);
              return (
                <label key={opt.id} className={`msf-option ${isChecked ? 'msf-option--selected' : ''}`}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleOption(opt.id)}
                    className="msf-checkbox"
                  />
                  <span className="msf-option-label">{opt.name}</span>
                </label>
              );
            })}
            {filtered.length === 0 && (
              <div className="msf-empty">No matches</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
